import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getChapterById, getChaptersByNovel } from '../services/api';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const ChapterReader = () => {
  const { novelId, chapterId } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [allChapters, setAllChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChapterData();
  }, [chapterId]);

  const fetchChapterData = async () => {
    try {
      const [chapterData, chaptersData] = await Promise.all([
        getChapterById(chapterId),
        getChaptersByNovel(novelId)
      ]);
      setChapter(chapterData);
      setAllChapters(chaptersData);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error fetching chapter:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-gray-600">Loading...</p></div>;
  }

  if (!chapter) {
    return <div className="flex items-center justify-center min-h-screen"><p className="text-gray-600">Chapter not found</p></div>;
  }

  const currentIndex = allChapters.findIndex(ch => ch.id === chapterId);
  const prevChapter = currentIndex > 0 ? allChapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < allChapters.length - 1 ? allChapters[currentIndex + 1] : null;

  return (
    <div className="py-16 px-6">
      <article className="max-w-3xl mx-auto">
        <Link to={`/novel/${novelId}`} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft size={16} />Back to Novel
        </Link>

        <header className="mb-12">
          <div className="text-sm uppercase tracking-wider text-gray-500 mb-2">Chapter {chapter.chapterNumber}</div>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">{chapter.title}</h1>
          <p className="text-sm text-gray-500">{new Date(chapter.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </header>

        <div className="prose prose-lg max-w-none mb-12">
          {chapter.content.split('\n\n').map((paragraph, index) => (
            <p key={index} className="text-gray-800 leading-relaxed mb-6">{paragraph}</p>
          ))}
        </div>

        <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
          {prevChapter ? (
            <Button onClick={() => navigate(`/novel/${novelId}/chapter/${prevChapter.id}`)} variant="outline" className="flex items-center gap-2">
              <ChevronLeft size={16} />Previous Chapter
            </Button>
          ) : <div></div>}
          {nextChapter && (
            <Button onClick={() => navigate(`/novel/${novelId}/chapter/${nextChapter.id}`)} className="bg-gray-900 text-white hover:bg-gray-800 flex items-center gap-2">
              Next Chapter<ChevronRight size={16} />
            </Button>
          )}
        </div>
      </article>
    </div>
  );
};

export default ChapterReader;
